import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountInfo, AuthenticationResult, InteractionStatus, PromptValue } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { LanguageSwitcherComponent } from './layout/language-switcher/language-switcher.component';
import { TranslatePipe } from './shared/pipes/translate.pipe';
import { ToastComponent } from './shared/components/toast/toast.component';
import { ClickOutsideDirective } from './shared/directives/click-outside.directive';
import { AuthGroupService } from './core/services/auth-group.service';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, LanguageSwitcherComponent, TranslatePipe, ToastComponent, ClickOutsideDirective],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authGroupService = inject(AuthGroupService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeAccount = signal<AccountInfo | null>(null);
  protected readonly isAuthenticated = computed(() => this.activeAccount() !== null);
  protected readonly hasRequiredGroup = signal(false);
  protected readonly isAuthorized = computed(() => this.isAuthenticated() && this.hasRequiredGroup());

  protected readonly isUserMenuOpen = signal(false);
  protected readonly loggedInEmail = computed(() => {
    const account = this.activeAccount();
    if (!account) {
      return '';
    }

    const claims = account.idTokenClaims as { preferred_username?: string; email?: string; upn?: string; } | undefined;
    return claims?.preferred_username?.trim() || claims?.email?.trim() || claims?.upn?.trim() || account.username || '';
  });

  ngOnInit(): void {
    let redirectAccount: AccountInfo | null = null;

    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult | null) => {
        redirectAccount = result?.account ?? null;
      },
      error: (error: unknown) => {
        console.error('MSAL redirect processing failed', error);
      },
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.resolveActiveAccount(redirectAccount);
        redirectAccount = null;
      });
  }

  private resolveActiveAccount(redirectAccount: AccountInfo | null): void {
    if (redirectAccount) {
      this.setActiveAccount(redirectAccount);
      return;
    }

    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length === 1) {
      this.setActiveAccount(accounts[0]);
      return;
    }

    this.setActiveAccount(null);

    if (accounts.length > 1) {
      // Multiple cached accounts and none marked active: don't guess which
      // one to use - let the user explicitly pick via the AAD account chooser.
      this.msalService.loginRedirect({
        scopes: ['user.read'],
        prompt: PromptValue.SELECT_ACCOUNT,
      }).subscribe();
    }
  }

  private setActiveAccount(account: AccountInfo | null): void {
    if (account) {
      this.msalService.instance.setActiveAccount(account);
    }

    this.activeAccount.set(account);
    this.hasRequiredGroup.set(account ? this.authGroupService.hasRequiredAzureGroup() : false);
  }

  protected toggleUserMenu(): void {
    this.isUserMenuOpen.update((isOpen) => !isOpen);
  }

  protected closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  protected logout(): void {
    this.closeUserMenu();
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/unauthenticated`,
    }).subscribe();
  }
}